import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import { RecipeEditorDrawer } from "../components/recipes/RecipeEditorDrawer";
import { BomViewerDrawer } from "../components/recipes/BomViewerDrawer";
import { Icon } from "../components/ui/Icon";
import { SearchSelect, type SSOption } from "../components/ui/SearchSelect";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { HardDeleteConfirm } from "../components/ui/HardDeleteConfirm";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard } from "../components/mobile/primitives";
import "./Items.css";

interface RecipeRow {
  id: string;
  producedKind: "semi" | "finished";
  producedId: string;
  producedCode: string;
  producedName: string;
  version: number;
  status: "draft" | "active" | "archived";
  mode: "percentage" | "absolute";
  batchSize: number;
  batchUom: string;
  expectedYieldPct: number;
  itemCount: number;
}

const STATUS_BADGE: Record<RecipeRow["status"], { cls: string; label: string }> = {
  draft: { cls: "", label: "Taslak" },
  active: { cls: "b-success", label: "Aktif" },
  archived: { cls: "b-warn", label: "Arşiv" },
};

// Filtre seçenekleri (statik enum — TÜR / DURUM / MOD sütunlarıyla aynı)
const TYPE_OPTIONS: SSOption[] = [
  { value: "finished", label: "Mamul" },
  { value: "semi", label: "Yarı Mamul" },
];
const STATUS_OPTIONS: SSOption[] = [
  { value: "draft", label: "Taslak" },
  { value: "active", label: "Aktif" },
  { value: "archived", label: "Arşiv" },
];
const MODE_OPTIONS: SSOption[] = [
  { value: "absolute", label: "Sabit Miktar" },
  { value: "percentage", label: "Yüzde (%)" },
];

export function RecipesPage() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Filtreler (select list) — Tür / Durum / Mod
  const [typeF, setTypeF] = useState<"" | "semi" | "finished">("");
  const [statusF, setStatusF] = useState<"" | "draft" | "active" | "archived">("");
  const [modeF, setModeF] = useState<"" | "absolute" | "percentage">("");
  const filtersActive = !!typeF || !!statusF || !!modeF;
  function clearFilters() { setTypeF(""); setStatusF(""); setModeF(""); setPage(1); }

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [copyFromId, setCopyFromId] = useState<string | null>(null);
  const [bom, setBom] = useState<{ kind: "semi" | "finished"; id: string; label: string } | null>(null);
  const [confirm, setConfirm] = useState<null | { title: string; message: ReactNode; tone: "danger" | "primary"; confirmLabel: string; run: () => void }>(null);

  const input = useMemo(() => ({
    page, pageSize,
    q: q.trim() || undefined,
    producedKind: typeF || undefined,
    status: statusF || undefined,
    mode: modeF || undefined,
  }), [page, q, typeF, statusF, modeF]);
  const recipesQ = trpc.recipe.list.useQuery(input);

  const activateMut = trpc.recipe.activate.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const archiveMut = trpc.recipe.archive.useMutation({ onSuccess: () => qc.invalidateQueries() });

  // Süper admin: çift onaylı kalıcı silme
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const isSuperAdmin = !!meQ.data?.user?.isSuperAdmin;
  /**
   * Reçete KIRILIMINI görme/düzenleme hakkı — `view:recipe` izni (uçtaki tek tanımın aynası:
   * apps/api/src/auth/permission.ts → receteKirilimiGorebilir).
   * ⚠ `isSuperAdmin` DEĞİL: o bayrak tesisId=null da şart koşar, tesise bağlı Sistem Yöneticisi
   * kendi tesisinin reçetesini bile göremezdi. KALICI SİL ayrı kalır — o yıkıcı işlem.
   */
  const receteGorunur = !!meQ.data?.user && (isSuperAdmin || (meQ.data.user.permissions ?? []).includes("view:recipe"));
  const [delTarget, setDelTarget] = useState<RecipeRow | null>(null);
  const hardDeleteMut = trpc.recipe.hardDelete.useMutation();
  function runHardDelete() {
    if (!delTarget) return;
    hardDeleteMut.mutate({ id: delTarget.id }, { onSuccess: () => { setDelTarget(null); hardDeleteMut.reset(); qc.invalidateQueries(); } });
  }

  const rows = (recipesQ.data?.rows ?? []) as unknown as RecipeRow[];
  const total = recipesQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function openCreate() {
    setEditingId(null);
    setCopyFromId(null);
    setEditorOpen(true);
  }
  function openEdit(id: string) {
    setEditingId(id);
    setCopyFromId(null);
    setEditorOpen(true);
  }
  function openCopy(id: string) {
    setEditingId(null);
    setCopyFromId(id);
    setEditorOpen(true);
  }
  function closeEditor() {
    setEditorOpen(false);
    setEditingId(null);
    setCopyFromId(null);
  }
  function handleActivate(r: RecipeRow) {
    setConfirm({
      title: "Reçeteyi yayına al?",
      message: <><b>{r.producedCode}</b> v{r.version} reçetesini yayına al? Önceki aktif versiyon arşivlenir.</>,
      tone: "primary",
      confirmLabel: "Yayına Al",
      run: () => activateMut.mutate({ id: r.id }),
    });
  }
  function handleArchive(r: RecipeRow) {
    setConfirm({
      title: "Reçeteyi arşivle?",
      message: <><b>{r.producedCode}</b> v{r.version} reçetesini arşivle?</>,
      tone: "danger",
      confirmLabel: "Arşivle",
      run: () => archiveMut.mutate({ id: r.id }),
    });
  }

  const drawers = (
    <>
      <RecipeEditorDrawer open={editorOpen} recipeId={editingId} copyFromId={copyFromId} onClose={closeEditor} />
      <BomViewerDrawer
        open={!!bom}
        producedKind={bom?.kind ?? null}
        producedId={bom?.id ?? null}
        producedLabel={bom?.label}
        onClose={() => setBom(null)}
      />

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title ?? ""}
        message={confirm?.message}
        tone={confirm?.tone ?? "primary"}
        confirmLabel={confirm?.confirmLabel ?? "Onayla"}
        cancelLabel="Vazgeç"
        onConfirm={() => { const c = confirm; setConfirm(null); c?.run(); }}
        onCancel={() => setConfirm(null)}
      />

      <HardDeleteConfirm
        open={!!delTarget}
        code={delTarget?.producedCode ?? ""}
        title="Reçeteyi kalıcı sil"
        warning={
          <>
            <b>{delTarget?.producedCode}</b> v{delTarget?.version} reçetesini <b>KALICI</b> olarak
            sileceksiniz. Reçete satırları da silinir. Bir iş emrinde kullanılmış veya bir ürün
            kartına bağlıysa silme engellenir.
          </>
        }
        busy={hardDeleteMut.isPending}
        errorMessage={hardDeleteMut.error?.message ?? null}
        onConfirmed={runHardDelete}
        onClose={() => { setDelTarget(null); hardDeleteMut.reset(); }}
      />
    </>
  );

  if (isMobile) {
    const MSTATUS: Record<RecipeRow["status"], { cls: string; label: string }> = {
      draft: { cls: "b-neutral", label: "Taslak" },
      active: { cls: "b-success", label: "Aktif" },
      archived: { cls: "b-warning", label: "Arşiv" },
    };
    return (
      <>
        <div className="m-stickytop">
          <button type="button" className="btn-md primary" onClick={openCreate}><Icon name="plus" size={15} />Yeni Reçete</button>
          <div className="m-search">
            <Icon name="search" size={15} />
            <input type="search" placeholder="Kod, ad, tür, mod, durum… ara" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
          </div>
          <div className="m-chips">
            {([["", "Hepsi"], ["active", "Aktif"], ["draft", "Taslak"], ["archived", "Arşiv"]] as Array<[typeof statusF, string]>).map(([v, lb]) => (
              <button key={lb} type="button" className={`m-chip${statusF === v ? " act" : ""}`} onClick={() => { setStatusF(v); setPage(1); }}>{lb}</button>
            ))}
          </div>
        </div>

        {recipesQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : recipesQ.isError ? (
          <div className="m-empty">Veri yüklenemedi</div>
        ) : rows.length === 0 ? (
          <div className="m-empty">{q || filtersActive ? "Sonuç yok" : "Henüz reçete yok"}</div>
        ) : (
          <>
            {rows.map((r) => {
              const sb = MSTATUS[r.status];
              return (
                <MCard pad key={r.id}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{r.producedName}</span>
                      <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{r.producedCode}</span>
                    </span>
                    <span className={`badge ${sb.cls} no-dot`}>{sb.label}</span>
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                    {([["Tür", r.producedKind === "semi" ? "Yarı M." : "Mamul"], ["Batch", `${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(r.batchSize)} ${r.batchUom}`], ["Ver.", `v${r.version}`], ["Satır", `${r.itemCount}`]] as Array<[string, string]>).map(([lb, v]) => (
                      <div key={lb} style={{ flex: 1 }}>
                        <div className="m-ovl">{lb}</div>
                        <div className="mono" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-800)", marginTop: 2 }}>{v}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                    {receteGorunur ? (
                      <>
                        <button type="button" className="m-iconbtn m-li-act" aria-label="Düzenle" onClick={() => openEdit(r.id)}><Icon name="edit" size={16} /></button>
                        <button type="button" className="m-iconbtn m-li-act" aria-label="Kopyala" onClick={() => openCopy(r.id)}><Icon name="copy" size={16} /></button>
                        <button type="button" className="m-iconbtn m-li-act" aria-label="BOM" onClick={() => setBom({ kind: r.producedKind, id: r.producedId, label: `${r.producedCode} — ${r.producedName}` })}><Icon name="list2" size={16} /></button>
                      </>
                    ) : (
                      <span className="m-iconbtn m-li-act" aria-label="Reçete kırılımı gizli" title="Reçete kırılımı mahremdir — görmek için “Reçete kırılımını görüntüle” (view:recipe) izni gerekir" style={{ cursor: "default", color: "var(--neutral-400)" }}><Icon name="lock" size={16} /></span>
                    )}
                    {r.status !== "active" ? (
                      <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--success)" }} aria-label="Yayına Al" onClick={() => handleActivate(r)} disabled={activateMut.isPending}><Icon name="play" size={16} /></button>
                    ) : (
                      <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--warning)" }} aria-label="Arşivle" onClick={() => handleArchive(r)} disabled={archiveMut.isPending}><Icon name="archive" size={16} /></button>
                    )}
                    {isSuperAdmin ? (
                      <button type="button" className="m-iconbtn m-li-act danger" aria-label="Kalıcı sil" onClick={() => { hardDeleteMut.reset(); setDelTarget(r); }}><Icon name="trash" size={16} /></button>
                    ) : null}
                  </div>
                </MCard>
              );
            })}
            {totalPages > 1 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
                <button type="button" className="btn-md secondary sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Önceki</button>
                <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{page} / {totalPages}</span>
                <button type="button" className="btn-md secondary sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Sonraki ›</button>
              </div>
            ) : null}
          </>
        )}
        {drawers}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Reçeteler</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Reçeteler</h1>
          <p className="sub">Ürün ağaçları (BOM) · versiyonlama · çok seviyeli patlatma</p>
        </div>
        <button type="button" className="btn-md primary" onClick={openCreate}><Icon name="plus" size={14} />Yeni Reçete</button>
      </div>

      <div className="dt2-wrap">
        <div className="dt2-toolbar">
          <div className="dt2-search-box">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
            </svg>
            <input type="search" placeholder="Kod, ad, tür, mod, durum… ara" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
          </div>

          <div className="dt2-filter sm">
            <SearchSelect
              value={typeF || null}
              options={TYPE_OPTIONS}
              onChange={(v) => { setTypeF((v as "semi" | "finished") ?? ""); setPage(1); }}
              placeholder="Tür"
              showFooter={false}
            />
          </div>
          <div className="dt2-filter">
            <SearchSelect
              value={modeF || null}
              options={MODE_OPTIONS}
              onChange={(v) => { setModeF((v as "absolute" | "percentage") ?? ""); setPage(1); }}
              placeholder="Mod"
              showFooter={false}
            />
          </div>
          <div className="dt2-filter sm">
            <SearchSelect
              value={statusF || null}
              options={STATUS_OPTIONS}
              onChange={(v) => { setStatusF((v as "draft" | "active" | "archived") ?? ""); setPage(1); }}
              placeholder="Durum"
              showFooter={false}
            />
          </div>
          {filtersActive ? (
            <button type="button" className="btn" onClick={clearFilters} title="Filtreleri temizle">
              <Icon name="x" size={13} />Temizle
            </button>
          ) : null}

          <span className="grow" />
          <button type="button" className="btn" onClick={() => recipesQ.refetch()} disabled={recipesQ.isFetching}>
            {recipesQ.isFetching ? "Yükleniyor…" : "Yenile"}
          </button>
        </div>

        <div className="dt2-scroll">
          {recipesQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : recipesQ.isError ? (
            <div className="empty-state"><p className="ttl" style={{ color: "var(--danger)" }}>Veri yüklenemedi</p><p className="sub">{recipesQ.error?.message}</p></div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <p className="ttl">{q || filtersActive ? "Sonuç yok" : "Henüz reçete yok"}</p>
              <p className="sub">
                {q ? `"${q}" için sonuç yok.` : filtersActive ? "Seçili filtrelere uyan reçete yok." : "İlk reçeteyi oluşturun."}
              </p>
              {q || filtersActive ? (
                <button type="button" className="btn-md es-action" onClick={() => { setQ(""); clearFilters(); }}>Filtreleri temizle</button>
              ) : (
                <button type="button" className="btn-md primary es-action" onClick={openCreate}><Icon name="plus" size={14} />Yeni Reçete</button>
              )}
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th>Üretilen Ürün</th>
                  <th style={{ width: 70 }}>Tür</th>
                  <th style={{ width: 60 }}>Ver.</th>
                  <th className="num" style={{ width: 130 }}>Batch</th>
                  <th style={{ width: 70 }}>Mod</th>
                  <th className="num" style={{ width: 80 }}>Satır</th>
                  <th style={{ width: 80 }}>Durum</th>
                  <th style={{ width: 110 }}>Eylemler</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const sb = STATUS_BADGE[r.status];
                  return (
                    <tr key={r.id}>
                      <td>
                        <span className="cell-id">{r.producedCode}</span>
                        <span className="sec">{r.producedName}</span>
                      </td>
                      <td><span className="badge-pill">{r.producedKind === "semi" ? "Yarı M." : "Mamul"}</span></td>
                      <td className="mono" style={{ fontSize: 12 }}>v{r.version}</td>
                      <td className="cell-num">{new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(r.batchSize)}<span className="unit">{r.batchUom}</span></td>
                      <td style={{ fontSize: 11.5, color: "var(--neutral-600)" }}>{r.mode === "percentage" ? "%" : "Sabit"}</td>
                      <td className="cell-num">{r.itemCount}</td>
                      <td><span className={`badge-pill ${sb.cls}`}>{sb.label}</span></td>
                      <td>
                        <div className="row-acts">
                          {receteGorunur ? (
                            <>
                              <button type="button" className="iact" aria-label="Düzenle" title="Düzenle" onClick={() => openEdit(r.id)}>
                                <Icon name="edit" size={15} />
                              </button>
                              <button type="button" className="iact" aria-label="Kopyala" title="Kopyala (hedef ürün seçip revize et)" onClick={() => openCopy(r.id)}>
                                <Icon name="copy" size={15} />
                              </button>
                              <button type="button" className="iact info" aria-label="BOM" title="BOM" onClick={() => setBom({ kind: r.producedKind, id: r.producedId, label: `${r.producedCode} — ${r.producedName}` })}>
                                <Icon name="list2" size={15} />
                              </button>
                            </>
                          ) : (
                            <span className="iact" aria-label="Reçete kırılımı gizli" title="Reçete kırılımı mahremdir — görmek için “Reçete kırılımını görüntüle” (view:recipe) izni gerekir" style={{ cursor: "default", color: "var(--neutral-400)" }}>
                              <Icon name="lock" size={15} />
                            </span>
                          )}
                          {r.status !== "active" ? (
                            <button type="button" className="iact success" aria-label="Yayına Al" title="Yayına Al" onClick={() => handleActivate(r)} disabled={activateMut.isPending}>
                              <Icon name="play" size={15} />
                            </button>
                          ) : (
                            <button type="button" className="iact warning" aria-label="Arşivle" title="Arşivle" onClick={() => handleArchive(r)} disabled={archiveMut.isPending}>
                              <Icon name="archive" size={15} />
                            </button>
                          )}
                          {isSuperAdmin ? (
                            <button type="button" className="iact danger" aria-label="Kalıcı sil" title="Kalıcı sil (süper admin)" onClick={() => { hardDeleteMut.reset(); setDelTarget(r); }}>
                              <Icon name="trash" size={15} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="dt2-foot">
          <div><b>{total}</b> kayıttan <b>{rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + rows.length}</b> arası</div>
          <div className="pager">
            <button type="button" className="pg" aria-label="Önceki sayfa" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span className="pg active">{page}</span>
            <span style={{ color: "var(--neutral-400)", padding: "0 8px", fontSize: 12 }}>/ {totalPages}</span>
            <button type="button" className="pg" aria-label="Sonraki sayfa" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>›</button>
          </div>
        </div>
      </div>

      {drawers}
    </div>
  );
}
